import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '../../../lib/api-fetcher';
import { Input } from '../../../components/ui/FormField';
import { Button } from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { DataView } from '../../../components/ui/DataView';
import { TableSkeleton } from '../../../components/ui/Skeleton';
import { emitToast } from '../../../components/ui/Toast';

interface SystemConfigItem {
  id: string;
  key?: string;
  configKey?: string;
  value?: string;
  configValue?: string;
  dataType: string;
  category: string;
  description: string | null;
  companyId: string | null;
  updatedAt: string;
}

export function ConfigPage() {
  const queryClient = useQueryClient();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const query = useQuery({
    queryKey: ['admin', 'config'],
    queryFn: async () => {
      const res = await fetcher.get<SystemConfigItem[] | { data: SystemConfigItem[] }>('/staff/v1/admin/config');
      return Array.isArray(res) ? res : res?.data ?? [];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      return fetcher.put(`/staff/v1/admin/config/${key}`, { value });
    },
    onSuccess: (_result, { key }) => {
      emitToast({
        type: 'success',
        title: 'Configuration updated',
        description: `${key} saved successfully.`,
      });
      setEditingKey(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'config'] });
    },
    onError: (err: Error, { key }) => {
      emitToast({
        type: 'error',
        title: 'Update failed',
        description: err?.message || `Could not save ${key}.`,
      });
    },
  });

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">System Configuration</h1>
          <p className="text-sm text-text-secondary">
            View and adjust company-wide financial and operational parameters.
          </p>
        </div>
      </div>

      <DataView
        query={query}
        loadingSkeleton={<TableSkeleton columns={5} rows={8} />}
        emptyTitle="No configuration parameters found"
        emptyDescription="When the platform defines system parameters, they will appear here."
      >
        {(items) => (
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-surface-2">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-medium text-text-secondary text-xs uppercase tracking-wider">
                        Parameter
                      </th>
                      <th className="px-4 py-2.5 text-left font-medium text-text-secondary text-xs uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-4 py-2.5 text-left font-medium text-text-secondary text-xs uppercase tracking-wider">
                        Value
                      </th>
                      <th className="px-4 py-2.5 text-left font-medium text-text-secondary text-xs uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-2.5 text-right font-medium text-text-secondary text-xs uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-surface-1">
                    {items.map((item) => {
                      const itemKey = item.key ?? item.configKey ?? item.id;
                      const itemVal = item.value ?? item.configValue ?? '';
                      const isEditing = editingKey === itemKey;

                      const startEdit = () => {
                        setEditingKey(itemKey);
                        setEditValue(itemVal);
                      };
                      const cancelEdit = () => setEditingKey(null);
                      const saveEdit = () => updateMutation.mutate({ key: itemKey, value: editValue });

                      return (
                        <tr key={itemKey} className="hover:bg-surface-2">
                          <td className="px-4 py-4 align-top">
                            <div className="font-medium text-text-primary">{itemKey}</div>
                            {item.description && (
                              <div className="mt-0.5 text-xs text-text-secondary">{item.description}</div>
                            )}
                            {item.companyId === null && (
                              <span className="mt-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-caption font-mono bg-surface-2 text-text-secondary border border-border">
                                Platform Default
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-text-secondary capitalize whitespace-nowrap">
                            {item.category || 'General'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {isEditing ? (
                              <Input
                                type="text"
                                value={editValue}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  setEditValue(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEdit();
                                  if (e.key === 'Escape') cancelEdit();
                                }}
                                className="w-48 font-mono"
                              />
                            ) : (
                              <code className="inline-block px-1.5 py-0.5 rounded text-xs font-mono bg-surface-2 text-text-primary border border-border">
                                {itemVal}
                              </code>
                            )}
                          </td>
                          <td className="px-4 py-4 text-caption font-mono uppercase text-text-muted whitespace-nowrap">
                            {item.dataType || 'STRING'}
                          </td>
                          <td className="px-4 py-4 text-right whitespace-nowrap">
                            {isEditing ? (
                              <div className="inline-flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={saveEdit}
                                  loading={updateMutation.isPending}
                                >
                                  Save
                                </Button>
                                <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={updateMutation.isPending}>
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={startEdit}>
                                Edit
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </DataView>
    </div>
  );
}